// ANA-R1-04 — one shared lookup that maps a finding's raw machine class name
// to the model's canonical DISPLAY name, used by every user-facing AI-finding
// surface (List rows, viewer bbox labels, tiles, the AI-findings tab, and —
// already — the AI filter module).
//
// The walkthrough found the List widget showing the model's raw German
// `class_name` (e.g. `Potentielle_Eisabplatzungsstelle`, `Rezeptor`) while the
// AI Filter Module showed the `ML_*` display name, so an analyst could not
// correlate a finding row with its filter entry. LOCKED DECISION (OQ-A1-3 a):
// the model's display name is canonical EVERYWHERE user-facing; the raw
// `class_name` is never user-facing; English fallback where i18n-capable.
//
// The contract `AiFinding` exposes ONLY the raw `aiClassName` — no `classUuid`,
// no display name (verified against the lyse/v1 schema; the join lives
// server-side via `ai_findings.class_uuid`). So the ONLY key the client can
// join a finding to a model class on is the raw machine name. The model's
// class table (taxonomy/v1 `getAiFindingModel` → `AiFindingModelClass`) is the
// one source that carries BOTH the raw `className` AND the localized
// `displayName`, so this hook builds `rawClassName → displayName` from it. No
// contract change is needed or made.
//
// Reusing the AI Filter Module's data source (the DA-scoped `ai-class-facets`)
// to learn WHICH models this DA's findings span, then fetching each model's
// classes, keeps a single mapping for the whole surface (no per-widget joins).
//
// Unresolvable classes (the walkthrough saw `Vortex_groß_intakt` missing from
// the filter list — under separate investigation in ANA-R1-05) fall back to
// the raw `aiClassName` UNCHANGED, with `resolved: false` so the render sites
// can mark them visually unobtrusive instead of hiding the finding.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getTaxonomyBaseUrl } from "@aeriosiq/auth-client";
import type { GetAiFindingModelResponse } from "@aeriosiq/types";
import { useAiClassFacetsQuery } from "./queries.js";
import { useDataPointFilterStore } from "./state/dataPointFilterStore.js";
import { pickLocalized } from "../catalogues/localizedText.js";
import { aiModelKeys } from "./aiModelClassKeys.js";

export interface ResolvedClassName {
  /** The text to render: the model display name, or the raw name unchanged. */
  display: string;
  /**
   * False when the finding's class could not be mapped to a model class — the
   * `display` is then the raw `aiClassName` and the caller should render it
   * unobtrusively (see ANA-R1-05).
   */
  resolved: boolean;
}

export interface ClassDisplayNameLookup {
  /**
   * Resolve a finding's raw `aiClassName` to its canonical display name.
   * Unknown / empty input ⇒ the raw value unchanged, `resolved: false`.
   */
  resolve: (aiClassName: string | null | undefined) => ResolvedClassName;
  /** True while the model class tables are still loading. */
  isPending: boolean;
}

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

/**
 * Build the shared raw-class-name → display-name lookup for a data
 * acquisition. Resolves through the model classes of every AI model the DA's
 * findings span.
 */
export function useClassDisplayNames(
  dataAcquisitionId: string,
): ClassDisplayNameLookup {
  const api = useApi();
  // The global selection's L1+L2 scope the facets endpoint reads. We only need
  // the set of model ids the DA spans, which is invariant under the lower-level
  // selection, so any selection that the filter module already cached works.
  const selection = useDataPointFilterStore((s) => s.selection);
  // `includeEmpty` so EVERY class of the DA's models is covered, not only the
  // classes that currently have findings under the L1+L2 scope.
  const facetsQuery = useAiClassFacetsQuery(dataAcquisitionId, selection, {
    includeEmpty: true,
  });

  const modelIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of facetsQuery.data?.items ?? []) {
      if (it.modelId && it.modelId !== "unknown") ids.add(it.modelId);
    }
    return [...ids].sort();
  }, [facetsQuery.data]);

  const modelQueries = useQueries({
    queries: modelIds.map((modelId) => ({
      queryKey: aiModelKeys.detail(modelId),
      enabled: modelId.length > 0,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
      retry: 1,
      queryFn: async (): Promise<GetAiFindingModelResponse> => {
        const res = await api.get<GetAiFindingModelResponse>(
          `/ai-finding-models/${encodeURIComponent(modelId)}`,
          { baseURL: getTaxonomyBaseUrl() },
        );
        return res.data;
      },
    })),
  });

  // Map the RAW machine class name → display name across every model's classes.
  // Keyed on the raw `className` because that is the only field shared with the
  // finding's `aiClassName`. A class with no `displayName` falls back to its
  // raw name (so it still resolves, just to the same text).
  const byRawName = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of modelQueries) {
      for (const c of q.data?.classes ?? []) {
        const display = pickLocalized(c.displayName) || c.className;
        if (c.className) map.set(c.className, display);
      }
    }
    return map;
  }, [modelQueries]);

  const isPending =
    facetsQuery.isPending ||
    (modelIds.length > 0 && modelQueries.some((q) => q.isPending));

  return useMemo<ClassDisplayNameLookup>(
    () => ({
      isPending,
      resolve: (aiClassName) => {
        if (!aiClassName)
          return { display: aiClassName ?? "", resolved: false };
        const display = byRawName.get(aiClassName);
        if (display === undefined) {
          return { display: aiClassName, resolved: false };
        }
        return { display, resolved: true };
      },
    }),
    [byRawName, isPending],
  );
}
